import { SPEAKERS_2027 } from "../data/eventData";
import { MaskLine, Reveal } from "./Motion";

const monogram = (name) =>
  name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

// Silhueta de palestrante para os cartões "Em breve!"
const Silhouette = () => (
  <svg viewBox="0 0 24 24" className="h-32 sm:h-40 w-auto text-white/[0.07]" fill="currentColor" aria-hidden="true">
    <path d="M12 11.5a5 5 0 1 0-5-5 5 5 0 0 0 5 5Zm0 1.8c-4.2 0-8 2.2-8 5.2V20h16v-1.5c0-3-3.8-5.2-8-5.2Z" />
  </svg>
);

const PlaceholderCard = ({ i }) => (
  <Reveal delay={0.06 * (i % 4)} className="h-full">
    <div
      className="relative block w-full h-full rounded-2xl overflow-hidden border border-dashed border-white/15 bg-brand-elev/50"
      data-testid={`speaker-placeholder-${i}`}
    >
      <div className="relative aspect-[4/5] flex flex-col">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-royal/40 via-brand-elev to-brand-base" />
        <div className="flex-1 flex items-center justify-center relative">
          <Silhouette />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-brand-deep/95 via-brand-deep/25 to-transparent pointer-events-none" />
        <h3 className="absolute bottom-16 sm:bottom-[4.5rem] left-4 right-4 font-display font-extrabold uppercase text-white/70 text-base sm:text-lg leading-[1.1]">
          Em breve!
        </h3>
        <div className="absolute bottom-3 left-3 right-3 rounded-xl bg-white/[0.08] backdrop-blur-xl border border-white/15 shadow-[inset_0_1px_0_rgba(255,255,255,0.10)] px-3.5 py-2.5">
          <p className="text-[11px] sm:text-xs text-brand-silver/50 leading-snug">Palestrante a definir</p>
        </div>
      </div>
    </div>
  </Reveal>
);

const SpeakerCard = ({ s, i, onSelect }) => (
  <Reveal delay={0.06 * (i % 4)} className="h-full">
    <button
      onClick={() => onSelect(s)}
      className="group relative block w-full text-left rounded-2xl overflow-hidden border border-white/10 bg-white/[0.04] backdrop-blur-sm transition-all duration-500 hover:border-white/30 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_rgba(2,8,23,0.60)]"
      data-testid={`speaker-card-${i}`}
    >
      {/* Foto */}
      <div className="relative aspect-[4/5] overflow-hidden bg-[radial-gradient(120%_90%_at_50%_0%,#1B2E6B_0%,#0C1530_72%)]">
        {s.photo ? (
          <img
            src={s.photo}
            alt={s.name}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-700"
            style={{ objectPosition: s.photoPosition || "center 20%" }}
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-brand-royal via-brand-elev to-brand-base flex flex-col items-center justify-center gap-3">
            <span className="font-display font-black text-6xl sm:text-7xl text-outline-cyan">
              {monogram(s.name)}
            </span>
            <span className="font-mono text-[9px] uppercase tracking-[0.26em] text-white/70 border border-white/25 rounded-full px-3 py-1">
              Foto em breve
            </span>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-brand-deep/95 via-brand-deep/25 to-transparent pointer-events-none" />

        {/* Nome sobre a foto · padrão G4 */}
        <h3 className="absolute bottom-16 sm:bottom-[4.5rem] left-4 right-4 font-display font-extrabold uppercase text-white text-base sm:text-lg leading-[1.1] drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)]">
          {s.short || s.name}
        </h3>

        {/* Caixa de cargo · vidro escuro sobre a base da foto */}
        <div className="absolute bottom-3 left-3 right-3 rounded-xl bg-white/[0.08] backdrop-blur-xl border border-white/15 shadow-[inset_0_1px_0_rgba(255,255,255,0.10)] px-3.5 py-2.5 group-hover:border-white/30 transition-colors duration-300">
          <p className="text-[11px] sm:text-xs text-brand-silver/85 leading-snug">{s.role}</p>
        </div>
      </div>
    </button>
  </Reveal>
);

export default function Speakers({ onSelect }) {
  return (
    <section id="autoridades" className="relative py-20 sm:py-28 bg-gradient-to-b from-brand-royal/25 via-brand-elev/30 to-brand-base border-y border-white/10 overflow-hidden" data-testid="speakers-section">
      {/* Backdrop oficial PS27 · sutil */}
      <img
        src="/speakers-backdrop.jpg"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover opacity-40 pointer-events-none select-none"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-brand-base/60 via-brand-base/30 to-brand-base/80 pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-5 sm:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="heading-lg" data-testid="speakers-heading">
            <MaskLine inView>
              <span className="text-gradient-blue">Palestrantes</span> confirmados
            </MaskLine>
            <MaskLine inView delay={0.12}>para o PS27</MaskLine>
          </h2>
          <Reveal delay={0.2}>
            <p className="mt-4 text-white/60 text-sm sm:text-base">O melhor line-up das licitações do Brasil.</p>
          </Reveal>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6" data-testid="speakers-grid">
          {SPEAKERS_2027.map((s, i) => (
            <SpeakerCard key={s.name} s={s} i={i} onSelect={onSelect} />
          ))}
          {Array.from({ length: 3 }).map((_, i) => (
            <PlaceholderCard key={`ph-${i}`} i={i + SPEAKERS_2027.length} />
          ))}
        </div>

        <Reveal delay={0.15}>
          <p className="mt-10 text-center text-sm text-white/45">
            Acompanhe as próximas confirmações
          </p>
        </Reveal>
      </div>
    </section>
  );
}
